import clsx from 'clsx';
import { forwardRef, type ReactNode } from 'react';

import { avatarSubtitleCSS } from './Avatar.sty.js';
import { DataTestId } from '../../core/types/data-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { canReceiveDefaultTextFormat as _canReceiveDefaultTextFormat } from '../../core/utils/can-receive-default-text-format.js';
import { textStyle as textStyleCSS } from '../../styles/textStyle.sty.js';


//#region Typings
/**
 * Accepted properties for the Avatar Subtitle
 * @public
 */
export interface AvatarSubtitleProps extends StylingProps, DataTestId {
  /** Elements to be displayed in the Avatar Subtitle slot. */
  children: ReactNode;
}
//#endregion

/**
 * Avatar Subtitle component
 * @public
 */
export const Subtitle = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  AvatarSubtitleProps
>((props, forwardedRef) => {
  const {
    children,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
  }: AvatarSubtitleProps = props;
  const childIsText = _canReceiveDefaultTextFormat(children);

  if (childIsText) {
    return (
      <div
        data-testid={dataTestId}
        ref={forwardedRef}
        className={clsx(
          consumerClassName,
          avatarSubtitleCSS,
          textStyleCSS({ textStyle: 'small' }),
        )}
        style={consumerStyle}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      data-testid={dataTestId}
      ref={forwardedRef}
      className={consumerClassName}
      style={consumerStyle}
    >
      {children}
    </div>
  );
});

(Subtitle as typeof Subtitle & { displayName: string }).displayName =
  'Avatar.Subtitle';
